import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { ScrollReveal, StaggerContainer, StaggerItem, TextReveal } from '@/components/motion'
import { companyValues, jobOpenings } from '@/data/about'
import { Highlight } from './Highlight'

const faded = 'text-[color-mix(in_srgb,var(--color-landing-gray-900)_66%,transparent)] dark:text-[color-mix(in_srgb,white_62%,transparent)]'

/**
 * Careers (#careers, linked from the nav): heading and lede, the values as a
 * hairline-ruled 2x2, then the open roles as outlined cards with tag boxes
 * and a small button.
 */
export function CareersSection() {
  return (
    <section id="careers" aria-labelledby="careers-title" className="landing-section scroll-mt-24">
      <div className="landing-container">
        <div className="mx-auto lg:max-w-[80%]">
          <TextReveal as="h2" className="landing-text-display-xl text-[var(--color-landing-gray-900)] dark:text-white">
            <span id="careers-title">
              Work <Highlight color="blue">with us.</Highlight>
            </span>
          </TextReveal>
          <ScrollReveal direction="none" delay={0.15}>
            <p className={`mt-8 max-w-[52ch] border-l border-[var(--color-landing-gray-300)] pl-5 landing-text-body-lg dark:border-[var(--color-landing-gray-700)] ${faded}`}>
              We're building document tools that actually understand how work gets done. Here is what we believe, and where we're hiring.
            </p>
          </ScrollReveal>

          {/* Values */}
          <div className="mt-20 grid gap-6 lg:grid-cols-[minmax(0,0.25fr)_minmax(0,1fr)] lg:gap-12">
            <p className={`landing-text-body-lg ${faded}`}>What we believe</p>
            <StaggerContainer className="grid gap-x-12 sm:grid-cols-2" amount={0.05}>
              {companyValues.map((value) => (
                <StaggerItem key={value.title} className="border-t border-[var(--color-landing-gray-200)] py-8 dark:border-[var(--color-landing-gray-800)]">
                  <h3 className="font-[family-name:var(--font-landing-subdisplay)] text-xl font-bold uppercase tracking-[-0.01em] text-[var(--color-landing-gray-900)] dark:text-white">
                    {value.title}
                  </h3>
                  <p className="mt-3 text-pretty landing-text-body-sm text-[var(--color-landing-gray-700)] dark:text-[var(--color-landing-gray-300)]">
                    {value.description}
                  </p>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>

          {/* Open roles */}
          <div className="mt-16 grid gap-6 lg:grid-cols-[minmax(0,0.25fr)_minmax(0,1fr)] lg:gap-12">
            <p className={`landing-text-body-lg ${faded}`}>
              Open roles <span className="tabular-nums">({jobOpenings.length})</span>
            </p>
            <StaggerContainer className="grid gap-4 xl:grid-cols-2" amount={0.05} stagger={0.06}>
              {jobOpenings.map((job) => (
                <StaggerItem key={job.title} className="flex">
                  <article className="flex w-full flex-col justify-between gap-6 border border-[var(--color-landing-gray-300)] p-6 transition-colors duration-200 hover:border-[var(--color-landing-gray-900)] dark:border-[var(--color-landing-gray-700)] dark:hover:border-white md:p-7">
                    <div>
                      <h3 className="font-[family-name:var(--font-landing-subdisplay)] text-xl font-bold uppercase leading-tight tracking-[-0.01em] text-[var(--color-landing-gray-900)] dark:text-white md:text-2xl">
                        {job.title}
                      </h3>
                      <span aria-hidden className="mt-4 block h-px w-28 bg-[var(--color-landing-gray-300)] dark:bg-[var(--color-landing-gray-700)]" />
                      <p className={`mt-4 text-sm md:text-base ${faded}`}>{job.location}</p>
                    </div>
                    <div className="flex flex-wrap items-end justify-between gap-4">
                      <ul className="flex flex-wrap gap-2" aria-label="Tags">
                        <li className="bg-[var(--color-landing-accent)] px-2 py-1 text-xs font-bold uppercase tracking-wide text-white">
                          {job.department}
                        </li>
                        <li className="bg-[var(--color-landing-secondary)] px-2 py-1 text-xs font-bold uppercase tracking-wide text-white">
                          {job.type}
                        </li>
                      </ul>
                      <Link
                        to="/contact"
                        aria-label={`Apply for ${job.title}`}
                        className="landing-cta landing-cta--solid landing-cta--sm landing-btn-sweep ml-auto"
                      >
                        Apply
                        <ArrowRight aria-hidden className="h-4 w-4" />
                      </Link>
                    </div>
                  </article>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </div>
      </div>
    </section>
  )
}
